import React, { useEffect, useState } from 'react';
import { BulkPricingUpdatePayload } from '../../types';
import { Modal } from './Modal';

interface SetFreightCollectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: BulkPricingUpdatePayload) => void;
  carCount: number;
}

type PaymentOption = 'keep' | 'prepaid' | 'collect';

export const SetFreightCollectModal: React.FC<SetFreightCollectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carCount,
}) => {
  const [freightInput, setFreightInput] = useState('');
  const [collectInput, setCollectInput] = useState('');
  const [paymentOption, setPaymentOption] = useState<PaymentOption>('keep');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setFreightInput('');
    setCollectInput('');
    setPaymentOption('keep');
    setError(null);
  };

  const parseAmount = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (trimmed === '') {
      return undefined;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      return undefined;
    }
    return parsed;
  };

  const handleConfirm = () => {
    const payload: BulkPricingUpdatePayload = {};

    const freightValue = parseAmount(freightInput);
    if (freightInput.trim() !== '' && freightValue === undefined) {
      setError('Freight amount must be a non-negative number');
      return;
    }

    const collectValue = parseAmount(collectInput);
    if (collectInput.trim() !== '' && collectValue === undefined) {
      setError('Collect amount must be a non-negative number');
      return;
    }

    if (freightValue !== undefined) {
      payload.freight = freightValue;
    }

    if (paymentOption === 'prepaid') {
      payload.prepaid_or_collect = 'P';
      payload.clear_collect = true;
    } else if (paymentOption === 'collect') {
      payload.prepaid_or_collect = 'C';
    }

    if (paymentOption !== 'prepaid' && collectValue !== undefined) {
      payload.collect = collectValue;
    }

    if (
      payload.freight === undefined &&
      payload.collect === undefined &&
      payload.prepaid_or_collect === undefined &&
      !payload.clear_collect
    ) {
      setError('Specify at least one pricing change to apply');
      return;
    }

    onConfirm(payload);
    resetState();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Set Freight / Collect"
      size="md"
      footer={(
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Apply Changes
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Update pricing for {carCount} {carCount === 1 ? 'car' : 'cars'}. Leave a field blank to keep it unchanged.
        </p>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Freight amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={freightInput}
            onChange={(event) => setFreightInput(event.target.value)}
            placeholder="Enter freight amount"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-between">
            <span>Collect amount</span>
            {paymentOption === 'prepaid' && (
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                Collect will be cleared when set as prepaid
              </span>
            )}
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={collectInput}
            onChange={(event) => {
              setCollectInput(event.target.value);
              // Auto-select "Collect" when a collect amount is entered
              if (event.target.value.trim() !== '' && paymentOption !== 'collect') {
                setPaymentOption('collect');
              }
            }}
            placeholder="Enter collect amount"
            disabled={paymentOption === 'prepaid'}
            className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
              paymentOption === 'prepaid' ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          />
        </div>

        <div className="space-y-3">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment category</span>
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="radio"
                name="payment-option"
                value="keep"
                checked={paymentOption === 'keep'}
                onChange={() => setPaymentOption('keep')}
              />
              <span>Keep existing</span>
            </label>
            <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="radio"
                name="payment-option"
                value="prepaid"
                checked={paymentOption === 'prepaid'}
                onChange={() => setPaymentOption('prepaid')}
              />
              <span>Set as Prepaid (P)</span>
            </label>
            <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="radio"
                name="payment-option"
                value="collect"
                checked={paymentOption === 'collect'}
                onChange={() => setPaymentOption('collect')}
              />
              <span>Set as Collect (C)</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}
      </div>
    </Modal>
  );
};

export default SetFreightCollectModal;
